import React, {
    useMemo,
    useState,
    useCallback,
    useRef,
    useLayoutEffect,
} from 'react'
import {
    Box,
    Flex,
    Text,
    useColorModeValue,
} from '@chakra-ui/react'
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import { VariableSizeList as List } from 'react-window'

// NEW imports for Markdown
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import { markdownComponents } from '../../utils/constants';

export const PdfResponseTable = ({
                                     data,
                                     height = 600,
                                     collapsedRowHeight = 80,
                                     width = '100%',
                                     onCitationClick,
                                 }) => {
    const items = useMemo(() => Object.entries(data), [data])
    const [expandedRows, setExpandedRows] = useState(new Set())
    const sizeMap = useRef({})
    const listRef = useRef()

    const getItemSize = idx => sizeMap.current[idx] || collapsedRowHeight
    const setItemSize = (idx, size) => {
        if (sizeMap.current[idx] !== size) {
            sizeMap.current[idx] = size
            if (listRef.current?.resetAfterIndex) {
                listRef.current.resetAfterIndex(idx, true)
            }
        }
    }

    const toggleRow = useCallback(idx => {
        setExpandedRows(prev => {
            const next = new Set(prev)
            next.has(idx) ? next.delete(idx) : next.add(idx)
            return next
        })
    }, [])

    // theme tokens
    const headerBg    = useColorModeValue('white',   'gray.900')
    const rowBgEven   = useColorModeValue('white',   'gray.800')
    const rowBgOdd    = useColorModeValue('gray.50', 'gray.700')
    const borderColor = useColorModeValue('gray.200','gray.600')
    const hoverBg     = useColorModeValue('gray.100','gray.600')
    const shadow      = useColorModeValue('sm',      'dark-lg')

    const Row = ({ index, style }) => {
        const [pdfName, answer] = items[index]
        const isExpanded       = expandedRows.has(index)
        const rowRef           = useRef()
        const textRef          = useRef()
        const bg               = index % 2 === 0 ? rowBgEven : rowBgOdd

        // decide if “show more” is needed
        const isLong = answer.split('\n').join(' ').length > 180

        // measure on mount & whenever expanded toggles
        useLayoutEffect(() => {
            if (rowRef.current && textRef.current) {
                const textH = textRef.current.getBoundingClientRect().height
                const style = window.getComputedStyle(rowRef.current)
                const padY  = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
                const bdY   = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
                // + extra margin for the “Show more” link itself
                const total = textH + padY + bdY + (isLong ? 24 : 0)
                setItemSize(index, total)
            }
        }, [index, isExpanded])

        return (
            <Flex
                ref={rowRef}
                style={style}
                bg={bg}
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor={borderColor}
                _hover={{ bg: hoverBg }}
            >
                {/* PDF Name */}
                <Box flex="0 0 27%" pr={4}>
                    <Text
                        as="span"
                        fontSize="sm"
                        fontWeight="500"
                        wordBreak="break-word"
                        onClick={() => onCitationClick(pdfName, 1)}
                        cursor="pointer"
                        color="blue.500"
                        textDecoration="underline"
                        _hover={{ textDecoration: 'none', opacity: 0.8 }}
                    >
                        {pdfName}
                    </Text>
                </Box>

                {/* Markdown answer */}
                <Box flex="1">
                    <Box
                        ref={textRef}
                        fontSize="sm"
                        // clamp to ~2 lines when collapsed
                        maxH={isExpanded ? 'none' : '3rem'}
                        overflow="hidden"
                    >
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm, remarkBreaks]}
                            components={markdownComponents}
                        >
                            {answer}
                        </ReactMarkdown>
                    </Box>

                    {isLong && (
                        <Text
                            mt={1}
                            fontSize="xs"
                            fontWeight="500"
                            color="blue.500"
                            cursor="pointer"
                            display="inline-flex"
                            alignItems="center"
                            onClick={() => toggleRow(index)}
                        >
                            {isExpanded ? 'Show less' : 'Show more'}
                            {isExpanded
                                ? <ChevronUpIcon ml={1} boxSize={4}/>
                                : <ChevronDownIcon ml={1} boxSize={4}/>
                            }
                        </Text>
                    )}
                </Box>
            </Flex>
        )
    }

    const HEADER_HEIGHT = 56

    return (
        <Box
            width={width}
            height={`${height}px`}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="md"
            boxShadow={shadow}
            overflow="hidden"
            bg={useColorModeValue('white','gray.900')}
        >
            {/* sticky header */}
            <Flex
                position="sticky"
                top={0}
                bg={headerBg}
                zIndex={2}
                px={4}
                py={3}
                fontWeight="600"
                borderBottom="1px solid"
                borderColor={borderColor}
                borderTopRadius="md"
                align="center"
                boxShadow="sm"
            >
                <Box flex="0 0 30%" pr={4}>
                    <Text fontSize="xs" textTransform="uppercase" letterSpacing="widest">
                        Source Name
                    </Text>
                </Box>
                <Box flex="1">
                    <Text fontSize="xs" textTransform="uppercase" letterSpacing="widest">
                        Response
                    </Text>
                </Box>
            </Flex>

            {/* virtualized body */}
            <List
                ref={listRef}
                height={height - HEADER_HEIGHT}
                itemCount={items.length}
                itemSize={getItemSize}
                width="100%"
            >
                {Row}
            </List>
        </Box>
    )
}
